/**
 * Chat widget loader — the SDK is the mandatory install on customer sites, so
 * it also bootstraps the b2m-chat-widget bundle when the SDK's own script tag
 * asks for it. GTM story stays a single tag: the customer adds chat
 * attributes to the SDK tag they already have.
 *
 *   <script src=".../b2m-web-sdk.min.js"
 *     data-api-key="TRACKER_KEY"
 *     data-chat-integration-id="INTEGRATION_ID"
 *     data-chat-api-key="PUBLIC_CHAT_KEY"></script>
 *
 * SDK-tag attributes (all optional unless noted):
 *   data-chat-integration-id  activation + X-Integration-Id (this OR tenant required)
 *   data-chat-tenant          tenant preset id (e.g. "sporthink")
 *   data-chat-api-key         public chat key → X-API-Key
 *   data-chat-widget-url      bundle URL; defaults to b2m-chat-widget.js next
 *                             to the SDK bundle (same host/dir)
 *   data-chat-config, data-chat-config-url, data-chat-api-url,
 *   data-chat-auth-token, data-chat-logo-url, data-chat-position
 *
 * The loader injects the widget script with the attributes the widget's own
 * auto-init expects (data-chat-* → data-*). The widget detects the already
 * present SDK (window.b2m/b2mTrack) and never double-loads it.
 */

/**
 * Canonical production location of the widget bundle. The URL is normally
 * derived from the SDK tag's own src (both files deploy to the same dir), so
 * nobody writes it; this constant is the static fallback for setups where
 * derivation is impossible (inlined SDK, blob/src-less tags).
 */
const DEFAULT_WIDGET_URL = 'https://edge.b2metric.com/b2m-chat-widget.js';

/** SDK-tag attribute → widget-tag attribute. */
const FORWARDED = {
  'data-chat-widget-url': 'data-widget-url',
  'data-chat-tenant': 'data-tenant',
  'data-chat-integration-id': 'data-integration-id',
  'data-chat-api-key': 'data-chat-api-key',
  'data-chat-config': 'data-config',
  'data-chat-config-url': 'data-config-url',
  'data-chat-api-url': 'data-api-url',
  'data-chat-auth-token': 'data-auth-token',
  'data-chat-logo-url': 'data-logo-url',
  'data-chat-position': 'data-position'
};

/**
 * Inject the chat widget bundle when the SDK tag carries a chat identity.
 * Returns true when a widget script was injected.
 */
export function loadChatWidget(scriptTag, logger) {
  const attrs = {};
  for (const from in FORWARDED) {
    const value = scriptTag.getAttribute(from);
    if (value !== null) attrs[FORWARDED[from]] = value;
  }
  return loadChatWidgetFromConfig(attrs, scriptTag.src, logger);
}

/**
 * Same as loadChatWidget but from a plain config object — the path used by
 * window.b2mConfig / GTM tag-template installs where there is no attributed
 * script tag at all. `attrs` uses the WIDGET attribute names (data-tenant,
 * data-integration-id, data-chat-api-key, …).
 */
export function loadChatWidgetFromConfig(attrs, sdkSrc, logger) {
  if (!attrs['data-tenant'] && !attrs['data-integration-id']) return false;

  if (window.B2MChatWidget || document.querySelector('script[data-b2m-chat-widget]')) {
    logger && logger.log('[B2M SDK] Chat widget already present — skipping load');
    return false;
  }

  // Resolution: explicit override → next to the SDK bundle → static default.
  let src = attrs['data-widget-url'];
  if (!src) {
    try {
      src = new URL('b2m-chat-widget.js', sdkSrc).toString();
    } catch (e) {
      src = DEFAULT_WIDGET_URL;
      logger && logger.log('[B2M SDK] Chat widget URL falling back to static default');
    }
  }

  const widgetTag = document.createElement('script');
  widgetTag.src = src;
  widgetTag.async = true;
  widgetTag.setAttribute('data-b2m-chat-widget', 'true');
  for (const name in attrs) {
    if (name !== 'data-widget-url') widgetTag.setAttribute(name, attrs[name]);
  }

  document.head.appendChild(widgetTag);
  logger && logger.log(`[B2M SDK] Chat widget loading from ${src}`);
  return true;
}
